import { useEffect, useState } from 'react';

interface IndexStats {
  index: string;
  docsCount: number;
  storeSize: string;
  storeSizeBytes: number;
  health: string;
  status: string;
  category: string;
}

interface AggregationBucket {
  key: string;
  doc_count: number;
}

interface IndexBreakdown {
  indexName: string;
  field: string;
  buckets: AggregationBucket[];
  total: number;
}

interface StatisticsResponse {
  indexes: IndexStats[];
  breakdowns: {
    wpPages: IndexBreakdown;
    wpProducts: IndexBreakdown;
    wpVideos: IndexBreakdown;
    wpEvents: IndexBreakdown;
    wpNews: IndexBreakdown;
  };
  summary: {
    totalIndexes: number;
    totalDocuments: number;
    totalStorageBytes: number;
    totalStorage: string;
    byCategory: { category: string; count: number; docs: number }[];
  };
  generatedAt: string;
}

interface IndexStatisticsProps {
  apiUrl: string;
}

export function IndexStatistics({ apiUrl }: IndexStatisticsProps) {
  const [statistics, setStatistics] = useState<StatisticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'indexes' | 'pages' | 'products' | 'videos' | 'events' | 'news'>('overview');
  const [exporting, setExporting] = useState(false);

  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/statistics`);
      if (!response.ok) {
        throw new Error(`Failed to fetch statistics: ${response.status}`);
      }
      const data = await response.json();
      setStatistics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, [apiUrl]);

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      // Fetch export data from API
      const response = await fetch(`${apiUrl}/api/statistics/export`);
      if (!response.ok) {
        throw new Error(`Failed to fetch export data: ${response.status}`);
      }
      const exportData = await response.json() as {
        products: Record<string, unknown>[];
        pagesBySite: Record<string, Record<string, unknown>[]>;
        education: Record<string, unknown>[];
        news: Record<string, unknown>[];
      };

      // Dynamically import xlsx library
      const XLSX = await import('xlsx');
      const workbook = XLSX.utils.book_new();

      // Helper to truncate sheet name to 31 chars (Excel limit)
      const truncateSheetName = (name: string) => name.slice(0, 31);

      // Helper to convert array to comma-separated string
      const arrayToString = (arr: unknown): string => {
        if (Array.isArray(arr)) {
          return arr.join(', ');
        }
        return arr as string || '';
      };

      // Sheet 1: Products (with actual data from OpenSearch)
      if (exportData.products.length > 0) {
        const productHeaders = ['_id', 'id', 'title', 'sku', 'product_type', 'price', 'stock_status', 'permalink', 'published_date', 'Delete', 'Hide from Search', 'Hide from MAT-E', 'Product Type', 'Product Category', 'Subject'];
        const productRows = exportData.products.map(p => {
          return [
            p._id ?? null,
            p.id ?? null,
            p.title ?? null,
            p.sku ?? null,
            p.product_type ?? null,
            p.price ?? null,
            p.stock_status ?? null,
            p.permalink ?? null,
            p.published_date ?? null,
            false,  // Delete
            p.hide_from_search ?? false,  // Hide from Search
            p.hide_from_mate ?? false,  // Hide from MAT-E
            p.asm_product_type ?? null,  // Product Type (from asm_product_type)
            p.asm_product_category ?? null,  // Product Category (from asm_product_category)
            arrayToString(p.product_subject),  // Subject (from product_subject)
          ];
        });
        const productData = [productHeaders, ...productRows];
        const wsProducts = XLSX.utils.aoa_to_sheet(productData);
        XLSX.utils.book_append_sheet(workbook, wsProducts, 'Products');
      }

      // Sheets for each site (pages grouped by site_name)
      const pageHeaders = ['_id', 'id', 'wp_id', 'title', 'url', 'status', 'site_name', 'date_published', 'date_modified', 'Delete', 'Hide from Search', 'Hide from MAT-E'];
      for (const [siteName, pages] of Object.entries(exportData.pagesBySite)) {
        if (pages.length > 0) {
          const pageRows = pages.map(p => [
            p._id ?? null,
            p.id ?? null,
            p.wp_id ?? null,
            p.title ?? null,
            p.url ?? null,
            p.status ?? null,
            p.site_name ?? null,
            p.date_published ?? null,
            p.date_modified ?? null,
            false,  // Delete
            false,  // Hide from Search
            false,  // Hide from MAT-E
          ]);
          const pageData = [pageHeaders, ...pageRows];
          const wsPages = XLSX.utils.aoa_to_sheet(pageData);
          XLSX.utils.book_append_sheet(workbook, wsPages, truncateSheetName(siteName));
        }
      }

      // Sheet: Education (Events)
      if (exportData.education.length > 0) {
        const eventHeaders = ['_id', 'id', 'wp_id', 'title', 'url', 'status', 'site_name', 'date_published', 'date_modified', 'Delete', 'Hide from Search', 'Hide from MAT-E'];
        const eventRows = exportData.education.map(e => [
          e._id ?? null,
          e.id ?? null,
          e.wp_id ?? null,
          e.title ?? null,
          e.url ?? null,
          e.status ?? null,
          e.site_name ?? null,
          e.date_published ?? null,
          e.date_modified ?? null,
          false,  // Delete
          false,  // Hide from Search
          false,  // Hide from MAT-E
        ]);
        const eventData = [eventHeaders, ...eventRows];
        const wsEducation = XLSX.utils.aoa_to_sheet(eventData);
        XLSX.utils.book_append_sheet(workbook, wsEducation, 'Education');
      }

      // Sheet: ASM News
      if (exportData.news.length > 0) {
        const newsHeaders = ['_id', 'id', 'title', 'url', 'categories', 'date_published', 'date_modified', 'Delete', 'Hide from Search', 'Hide from MAT-E'];
        const newsRows = exportData.news.map(n => [
          n._id ?? null,
          n.id ?? null,
          n.title ?? null,
          n.url ?? null,
          arrayToString(n.categories),
          n.date_published ?? null,
          n.date_modified ?? null,
          false,  // Delete
          false,  // Hide from Search
          false,  // Hide from MAT-E
        ]);
        const newsData = [newsHeaders, ...newsRows];
        const wsNews = XLSX.utils.aoa_to_sheet(newsData);
        XLSX.utils.book_append_sheet(workbook, wsNews, 'ASM News');
      }

      // Generate filename with timestamp (format: opensearch_asmi_wp_indexes_YYYYMMDD_HHMMSS.xlsx)
      const now = new Date();
      const timestamp = now.getFullYear().toString() +
        (now.getMonth() + 1).toString().padStart(2, '0') +
        now.getDate().toString().padStart(2, '0') + '_' +
        now.getHours().toString().padStart(2, '0') +
        now.getMinutes().toString().padStart(2, '0') +
        now.getSeconds().toString().padStart(2, '0');
      const filename = `opensearch_asmi_wp_indexes_${timestamp}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export Excel file. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
        <span className="ml-3 text-zinc-600">Loading statistics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-600">
        <p className="font-medium">Error loading statistics</p>
        <p className="text-sm">{error}</p>
        <button
          type="button"
          onClick={fetchStatistics}
          className="mt-2 rounded bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!statistics) {
    return null;
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'indexes', label: 'All Indexes' },
    { id: 'pages', label: 'WP Pages' },
    { id: 'products', label: 'Products' },
    { id: 'videos', label: 'Videos' },
    { id: 'events', label: 'Education' },
    { id: 'news', label: 'News' },
  ] as const;

  return (
    <div className="space-y-6">
      {/* Header with export button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Index Statistics</h2>
          <p className="text-sm text-zinc-500">
            Generated: {new Date(statistics.generatedAt).toLocaleString()}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={fetchStatistics}
            disabled={loading}
            className="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={exportToExcel}
            disabled={exporting}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-zinc-200">
        <nav className="-mb-px flex space-x-4 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border border-zinc-200 bg-white p-4">
                <p className="text-sm text-zinc-500">Total Indexes</p>
                <p className="text-2xl font-bold text-zinc-900">{statistics.summary.totalIndexes}</p>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-white p-4">
                <p className="text-sm text-zinc-500">Total Documents</p>
                <p className="text-2xl font-bold text-zinc-900">{formatNumber(statistics.summary.totalDocuments)}</p>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-white p-4">
                <p className="text-sm text-zinc-500">Total Storage</p>
                <p className="text-2xl font-bold text-zinc-900">{statistics.summary.totalStorage}</p>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-white p-4">
                <p className="text-sm text-zinc-500">Categories</p>
                <p className="text-2xl font-bold text-zinc-900">{statistics.summary.byCategory.length}</p>
              </div>
            </div>

            {/* By Category */}
            <div className="rounded-lg border border-zinc-200 bg-white">
              <div className="border-b border-zinc-200 px-4 py-3">
                <h3 className="font-medium text-zinc-900">Documents by Category</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-zinc-600">Category</th>
                      <th className="px-4 py-2 text-right text-sm font-medium text-zinc-600">Indexes</th>
                      <th className="px-4 py-2 text-right text-sm font-medium text-zinc-600">Documents</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-200">
                    {statistics.summary.byCategory.map(cat => (
                      <tr key={cat.category} className="hover:bg-zinc-50">
                        <td className="px-4 py-2 text-sm text-zinc-900">{cat.category}</td>
                        <td className="px-4 py-2 text-right text-sm text-zinc-600">{cat.count}</td>
                        <td className="px-4 py-2 text-right text-sm text-zinc-900 font-medium">{formatNumber(cat.docs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'indexes' && (
          <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-zinc-600">Index Name</th>
                    <th className="px-4 py-2 text-right text-sm font-medium text-zinc-600">Documents</th>
                    <th className="px-4 py-2 text-right text-sm font-medium text-zinc-600">Size</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-zinc-600">Category</th>
                    <th className="px-4 py-2 text-center text-sm font-medium text-zinc-600">Health</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200">
                  {statistics.indexes.map(idx => (
                    <tr key={idx.index} className="hover:bg-zinc-50">
                      <td className="px-4 py-2 text-sm font-mono text-zinc-900">{idx.index}</td>
                      <td className="px-4 py-2 text-right text-sm text-zinc-900">{formatNumber(idx.docsCount)}</td>
                      <td className="px-4 py-2 text-right text-sm text-zinc-600">{idx.storeSize}</td>
                      <td className="px-4 py-2 text-sm text-zinc-600">{idx.category}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`inline-block h-2 w-2 rounded-full ${
                          idx.health === 'green' ? 'bg-green-500' :
                          idx.health === 'yellow' ? 'bg-yellow-500' :
                          idx.health === 'red' ? 'bg-red-500' : 'bg-zinc-400'
                        }`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'pages' && (
          <BreakdownTable
            title="WordPress Pages by Site"
            breakdown={statistics.breakdowns.wpPages}
            keyLabel="Site Name"
            countLabel="Page Count"
          />
        )}

        {activeTab === 'products' && (
          <BreakdownTable
            title="Products by Category"
            breakdown={statistics.breakdowns.wpProducts}
            keyLabel="Category"
            countLabel="Product Count"
          />
        )}

        {activeTab === 'videos' && (
          <BreakdownTable
            title="Videos"
            breakdown={statistics.breakdowns.wpVideos}
            keyLabel="Title"
            countLabel="Count"
          />
        )}

        {activeTab === 'events' && (
          <BreakdownTable
            title="Education (Events)"
            breakdown={statistics.breakdowns.wpEvents}
            keyLabel="Title"
            countLabel="Count"
          />
        )}

        {activeTab === 'news' && (
          <BreakdownTable
            title="ASM News by Category"
            breakdown={statistics.breakdowns.wpNews}
            keyLabel="Category"
            countLabel="Article Count"
          />
        )}
      </div>
    </div>
  );
}

interface BreakdownTableProps {
  title: string;
  breakdown: IndexBreakdown;
  keyLabel: string;
  countLabel: string;
}

function BreakdownTable({ title, breakdown, keyLabel, countLabel }: BreakdownTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  if (breakdown.buckets.length === 0) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-8 text-center text-zinc-500">
        No data available for {title.toLowerCase()}
      </div>
    );
  }

  const formatNumber = (num: number) => num.toLocaleString();

  const totalItems = breakdown.buckets.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const currentItems = breakdown.buckets.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
      <div className="border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
        <div>
          <h3 className="font-medium text-zinc-900">{title}</h3>
          <p className="text-sm text-zinc-500">Total: {formatNumber(breakdown.total)} documents ({formatNumber(totalItems)} items)</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-zinc-600">Per page:</label>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="rounded border border-zinc-300 px-2 py-1 text-sm"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={totalItems}>All</option>
          </select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-50">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium text-zinc-600">{keyLabel}</th>
              <th className="px-4 py-2 text-right text-sm font-medium text-zinc-600">{countLabel}</th>
              <th className="px-4 py-2 text-right text-sm font-medium text-zinc-600">% of Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {currentItems.map(bucket => (
              <tr key={bucket.key} className="hover:bg-zinc-50">
                <td className="px-4 py-2 text-sm text-zinc-900">{bucket.key}</td>
                <td className="px-4 py-2 text-right text-sm text-zinc-900 font-medium">
                  {formatNumber(bucket.doc_count)}
                </td>
                <td className="px-4 py-2 text-right text-sm text-zinc-500">
                  {((bucket.doc_count / breakdown.total) * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="border-t border-zinc-200 px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-zinc-600">
            Showing {formatNumber(startIndex + 1)} - {formatNumber(endIndex)} of {formatNumber(totalItems)}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className="rounded px-2 py-1 text-sm text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ««
            </button>
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="rounded px-2 py-1 text-sm text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              «
            </button>
            <span className="px-3 py-1 text-sm text-zinc-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="rounded px-2 py-1 text-sm text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              »
            </button>
            <button
              type="button"
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              className="rounded px-2 py-1 text-sm text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              »»
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
